import { CommonModule, CurrencyPipe } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { Subject } from "rxjs";
import { RippleModule } from "primeng/ripple";

// PrimeNG imports
import { MenuItem } from "primeng/api";
import { ButtonModule } from "primeng/button";
import { Menu, MenuModule } from "primeng/menu";
import { ProgressSpinnerModule } from "primeng/progressspinner";
import { SkeletonModule } from "primeng/skeleton";
import { TableModule } from "primeng/table";
import { TagModule } from "primeng/tag";
import { TooltipModule } from "primeng/tooltip";
import {
  TableSkeletonRowsComponent,
  SkeletonColumnConfig,
  SKELETON_PRESETS,
} from "../../../../shared/components/table-skeleton";

// Our interfaces
import { PAGE_SIZE_OPTIONS } from "../../interfaces/customer-filter.interface";
import {
  CustomerListResponse,
  CustomerSummary,
} from "../../interfaces/customer.interface";

/**
 * Page event interface for pagination
 */
export interface PageEvent {
  first: number;
  rows: number;
  page: number;
  pageCount: number;
}

/**
 * Sort event interface
 */
export interface SortEvent {
  field: string;
  order: 1 | -1;
}

/**
 * Row action type
 */
export type CustomerRowAction =
  | "edit"
  | "viewScheduledOrders"
  | "searchScheduledOrders"
  | "viewHistory"
  | "delete"
  | "restore";

/**
 * Customers Table Component
 * PrimeNG p-table implementation with row actions
 */
@Component({
  selector: "app-customers-table",
  standalone: true,
  imports: [
    CommonModule,
    CurrencyPipe,
    FormsModule,
    TableModule,
    ButtonModule,
    MenuModule,
    ProgressSpinnerModule,
    SkeletonModule,
    TagModule,
    TooltipModule,
    RippleModule,
    TableSkeletonRowsComponent,
  ],
  templateUrl: "./customers-table.component.html",
  styleUrl: "./customers-table.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomersTableComponent implements OnDestroy, OnChanges {
  // Input/Output pattern
  @Input() data: CustomerListResponse | null = null;
  @Input() loading = false;
  @Input() scrollHeight = "flex";

  @Output() sortChanged = new EventEmitter<SortEvent>();
  @Output() pageChanged = new EventEmitter<PageEvent>();
  @Output() rowAction = new EventEmitter<{
    action: CustomerRowAction;
    customer: CustomerSummary;
    searchTerm?: string;
  }>();

  // ViewChild references
  @ViewChild("contextMenu") contextMenu!: Menu;

  // Component state
  private readonly destroy$ = new Subject<void>();

  contextMenuItems: MenuItem[] = [];
  currentContextCustomer: CustomerSummary | null = null;

  // Row expansion state
  expandedRows: { [key: number]: boolean } = {};

  // Page size options - use shared constant from interface
  // Matches scheduled-orders pattern: [5, 10, 20, 25, 50, 100]
  readonly pageSizeOptions = [...PAGE_SIZE_OPTIONS];

  // Skeleton columns configuration
  readonly skeletonColumns: SkeletonColumnConfig[] = SKELETON_PRESETS.customers;

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["data"] && this.data) {
      // Data changed - could reset state if needed
    }
  }

  /**
   * Handle table sorting
   */
  onSort(event: { field: string; order: number }): void {
    const sortEvent: SortEvent = {
      field: event.field,
      order: event.order as 1 | -1,
    };
    this.sortChanged.emit(sortEvent);
  }

  /**
   * Handle table pagination
   */
  onPageChange(event: { first: number; rows: number }): void {
    const page = Math.floor(event.first / event.rows);
    const pageCount = Math.ceil(
      (this.data?.pagination?.totalItems || 0) / event.rows,
    );
    this.pageChanged.emit({
      first: event.first,
      rows: event.rows,
      page,
      pageCount,
    });
  }

  /**
   * Get customer full name
   * Uses PascalCase properties matching old QMC API response
   */
  getCustomerFullName(customer: CustomerSummary): string {
    const firstName = customer?.FirstName?.trim() || "";
    const lastName = customer?.LastName?.trim() || "";

    if (firstName && lastName) {
      return `${firstName} ${lastName}`;
    } else if (firstName) {
      return firstName;
    } else if (lastName) {
      return lastName;
    }
    return customer?.Email || "-";
  }

  /**
   * Format date
   */
  formatDate(date: Date | string | null | undefined): string {
    if (!date) return "-";
    const dateObj = typeof date === "string" ? new Date(date) : date;
    return dateObj.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }

  /**
   * Track by function for ngFor performance
   * Uses PascalCase Id matching API response
   */
  trackByCustomerId(index: number, customer: CustomerSummary): number {
    return customer?.Id || 0;
  }

  /**
   * Get row menu items for a specific customer
   * Uses PascalCase properties matching old QMC API response
   */
  getRowMenuItems(customer: CustomerSummary): MenuItem[] {
    const isDeleted = !customer.Active;

    // If customer is deleted, show restore option only
    if (isDeleted) {
      return [
        {
          label: "Restore Customer",
          icon: "pi pi-refresh",
          command: () => this.rowAction.emit({ action: "restore", customer }),
        },
      ];
    }

    const items: MenuItem[] = [
      {
        label: "Edit Customer",
        icon: "pi pi-pencil",
        command: () => this.rowAction.emit({ action: "edit", customer }),
      },
    ];

    if ((customer.TotalScheduledOrdersCount || 0) > 0) {
      items.push({
        label: "View Scheduled Orders",
        icon: "pi pi-calendar",
        command: () =>
          this.rowAction.emit({ action: "viewScheduledOrders", customer }),
      });
    }

    items.push({
      label: "View History",
      icon: "pi pi-history",
      command: () => this.rowAction.emit({ action: "viewHistory", customer }),
    });

    items.push({ separator: true });

    items.push({
      label: "Delete Customer",
      icon: "pi pi-trash",
      command: () => this.rowAction.emit({ action: "delete", customer }),
    });

    return items;
  }

  /**
   * Toggle row menu
   */
  toggleRowMenu(event: Event, customer: CustomerSummary): void {
    this.currentContextCustomer = customer;
    this.contextMenuItems = this.getRowMenuItems(customer);
    this.contextMenu.toggle(event);
  }

  /**
   * Handle row click to edit customer
   */
  onRowClick(customer: CustomerSummary): void {
    this.rowAction.emit({ action: "edit", customer });
  }

  /**
   * Get scheduled orders badge severity
   */
  getScheduledOrdersBadgeSeverity(
    count: number,
    activeCount: number,
  ): "success" | "warn" | "secondary" {
    if (activeCount > 0) return "success";
    if (count > 0) return "warn";
    return "secondary";
  }

  /**
   * Handle row expand event
   */
  onRowExpand(event: { data: CustomerSummary }): void {
    // Single row mode - handled by PrimeNG
    this.expandedRows = { [event.data.Id]: true };
  }

  /**
   * Handle row collapse event
   */
  onRowCollapse(event: { data: CustomerSummary }): void {
    delete this.expandedRows[event.data.Id];
  }

  /**
   * Toggle row expansion (used for mobile card click)
   * Clicking on card (except ID link) expands/collapses the row
   */
  toggleRowExpansion(customer: CustomerSummary): void {
    if (this.expandedRows[customer.Id]) {
      delete this.expandedRows[customer.Id];
    } else {
      // Single row mode - collapse others and expand this one
      this.expandedRows = { [customer.Id]: true };
    }
  }

  /**
   * Send email to customer
   */
  sendEmail(customer: CustomerSummary): void {
    if (customer.Email) {
      window.location.href = `mailto:${customer.Email}`;
    }
  }

  /**
   * Search scheduled orders by a specific term (name or email)
   */
  searchScheduledOrders(customer: CustomerSummary, searchTerm: string): void {
    this.rowAction.emit({
      action: "searchScheduledOrders",
      customer,
      searchTerm,
    });
  }
}
